import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import Logo from "./Logo";
import { useBooking } from "./BookingDialog";
import FacialsLaunchBanner from "./FacialsLaunchBanner";
import HalloweenBanner from "./HalloweenBanner";

const links = [
  { label: "Services", href: "#services" },
  { label: "Facials", href: "#facials", isNew: true },
  { label: "About", href: "#about" },
  { label: "Gallery", href: "#gallery" },
  { label: "Gift Cards", href: "#gift-cards" },
  { label: "Blog", href: "/blog", route: true },
  { label: "Contact", href: "#contact" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openBooking } = useBooking();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Hero is navy → nav links/text need to be cream/gold initially, then navy on scroll
  const linkBaseColor = scrolled ? "text-[#0F1F4E]" : "text-[#F8F1D8]";
  const callColor = scrolled ? "text-[#0F1F4E]" : "text-[#EAD584]";
  const logoTone = scrolled ? "navy" : "gold";
  const location = useLocation();
  const onHome = location.pathname === "/";

  const renderAnchor = (l, testIdPrefix, className, onClick) => {
    // Route links (Blog) use react-router Link
    if (l.route) {
      return (
        <Link
          key={l.href}
          to={l.href}
          data-testid={`${testIdPrefix}-${l.label.toLowerCase().replace(/\s/g, "-")}`}
          className={className}
          onClick={onClick}
        >
          {l.label}
        </Link>
      );
    }
    // Anchor links work on home; off-home, prepend "/" so they navigate home first
    const href = onHome ? l.href : `/${l.href}`;
    return (
      <a
        key={l.href}
        href={href}
        data-testid={`${testIdPrefix}-${l.label.toLowerCase().replace(/\s/g, "-")}`}
        className={`${className} inline-flex items-center gap-1.5`}
        onClick={onClick}
      >
        {l.label}
        {l.isNew && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-[#EAD584] text-[#0F1F4E] text-[9px] uppercase tracking-widest font-semibold">
            New
          </span>
        )}
      </a>
    );
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#FAF6E8]/85 backdrop-blur-xl border-b border-[#D9CFA0]/70"
          : "bg-transparent"
      }`}
    >
      <div
        className={`overflow-hidden transition-all duration-500 ${
          scrolled ? "max-h-0 opacity-0" : "max-h-40 opacity-100"
        }`}
      >
        <HalloweenBanner />
        <FacialsLaunchBanner />
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-logo"
          className="flex items-center group"
        >
          <Logo tone={logoTone} size="md" showFrame={false} showSubtitle={false} className="transition-colors" />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) =>
            renderAnchor(
              l,
              "nav",
              `text-sm tracking-wide ${linkBaseColor} link-underline text-link-hover-gold transition-colors`
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="tel:9162435514"
            data-testid="header-call-link"
            className={`hidden md:inline-flex items-center gap-2 text-sm tracking-wide ${callColor} link-underline transition-colors`}
          >
            <Phone className="w-4 h-4" /> 916-243-5514
          </a>
          <button
            type="button"
            onClick={() => openBooking()}
            data-testid="header-book-now"
            className="btn-shine inline-flex items-center px-5 py-2.5 rounded-full text-sm tracking-wide text-[#0F1F4E] bg-[#EAD584] hover:bg-[#F0DC8A] transition-colors"
          >
            Book Now
          </button>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((o) => !o)}
            className={`lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border ${
              scrolled ? "border-[#0F1F4E] text-[#0F1F4E]" : "border-[#EAD584] text-[#EAD584]"
            }`}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 ${
          open ? "max-h-[480px]" : "max-h-0"
        } bg-[#FAF6E8]/95 backdrop-blur-xl border-b border-[#D9CFA0]/70`}
      >
        <nav className="px-6 py-6 flex flex-col gap-5">
          {links.map((l) =>
            renderAnchor(
              l,
              "mobile-nav",
              "font-serif text-2xl bb-ink",
              () => setOpen(false)
            )
          )}
          <a
            href="tel:9162435514"
            data-testid="mobile-call-link"
            className="text-sm bb-ink-soft inline-flex items-center gap-2"
          >
            <Phone className="w-4 h-4" /> 916-243-5514
          </a>
        </nav>
      </div>
    </header>
  );
}
